import Image from 'next/image';

import { TapPaymentWidget } from '@/components/atomic/atoms';
import { Button, Text } from '@/components/atomic/nuclei';
import { staticMediaStoreBaseURL } from '@/config/common';
import { getImageURL } from '@/helpers/carousel';
import { parseFormatPriceValueFromAPI } from '@/lib/numberStringUtils';
import { convertToStandardDateFormat } from '@/lib/time';
import { PAYMENT_SOURCE_TYPE } from '@/services/payment.service';

const NoDueOrderMessage = () => (
  <div className='text-white text-center text-sm font-medium mt-5'>
    <Text
      {...{
        content: '🎉 Hurray!! You are all set.'
      }}
    />
    <Text
      {...{
        content: 'There is no due amount for this order.'
      }}
    />
  </div>
);

const EventInfo = ({ icon, label }) => (
  <div className='flex gap-4'>
    <Image
      alt='cart'
      height={40}
      src={`${staticMediaStoreBaseURL}/icons/${icon}`}
      width={40}
    />
    <Text
      {...{
        className: 'text-sm md:text-base font-medium self-center',
        content: label
      }}
    />
  </div>
);

const EventMedia = ({ eventImage }) => (
  <div className='rounded-lg'>
    <Image
      alt='event media'
      className='rounded-lg'
      height={115}
      src={getImageURL(eventImage)}
      width={115}
    />
  </div>
);

const EventTitle = ({ eventName }) => (
  <Text
    {...{
      className: 'self-center text-xl font-medium',
      content: eventName
    }}
  />
);

const OrderTotal = ({ orderTotal }) => (
  <div className='flex justify-between'>
    <div className='flex gap-1'>
      <Text
        {...{
          className: 'text-sm md:text-base font-medium',
          content: 'Grand Total'
        }}
      />
      <Text
        {...{
          className: 'text-sm font-light leading-5 md:leading-6',
          content: '(AED)'
        }}
      />
    </div>
    <Text
      {...{
        className: 'text-sm md:text-base font-medium',
        content: orderTotal
      }}
    />
  </div>
);

const PriceBreakUp = ({ label, price, sublabel }) => (
  <div className='flex justify-between'>
    <div className='flex flex-col'>
      <div className='flex gap-1'>
        <Text
          {...{
            className: 'text-sm md:text-base font-medium',
            content: label
          }}
        />
        <Text
          {...{
            className: 'text-sm font-light leading-5 md:leading-6',
            content: '(AED)'
          }}
        />
      </div>
      <Text
        {...{
          className: 'text-sm font-light',
          content: sublabel
        }}
      />
    </div>
    <Text
      {...{
        className: 'text-sm md:text-base font-medium',
        content: price
      }}
    />
  </div>
);

const EventDetails = ({
  eventContactName,
  eventDate,
  eventImage,
  eventName,
  hostCreditEntity,
  itemNumber,
  paymentSourceType
}) => {
  const iconUrls = {
    calendar: 'calendar-red-with-frame.svg',
    cart: 'cart-red-with-frame.svg',
    hostCreditEntity: 'host-credit-entity-red-icon.svg',
    order: 'order-number-red.svg',
    user: 'user-red-with-frame.svg'
  };

  const eventInfo = [
    { icon: iconUrls[paymentSourceType], label: itemNumber },
    { icon: iconUrls.user, label: eventContactName },
    {
      icon: iconUrls.hostCreditEntity,
      label: hostCreditEntity?.name,
      show: Boolean(hostCreditEntity?.name)
    },
    { icon: iconUrls.calendar, label: convertToStandardDateFormat(eventDate) }
  ];

  return (
    <div className='flex flex-col md:w-1/2 gap-2 md:gap-4 rounded-t-2xl md:rounded-tr-none md:rounded-l-2xl p-4 md:p-8 bg-white'>
      <div className='flex gap-4'>
        <EventMedia {...{ eventImage }} />
        <EventTitle {...{ eventName }} />
      </div>
      {eventInfo
        .filter(({ show = true }) => show)
        .map(({ icon, label }, index) => (
          <EventInfo
            key={index}
            {...{ icon, label }}
          />
        ))}
    </div>
  );
};

const PaymentDetails = ({
  amountDue,
  amountPaid,
  duePriceBreakUpLabel,
  loginAndViewDetailsCTALabel,
  onClickLoginAndViewDetails,
  onClickPayAmount,
  orderTotal,
  payAmountCTALabel,
  paymentPriceBreakUpLabel,
  paymentSourceType
}) => {
  const showNoDuePaymentMessage = amountDue <= 0;
  const isPaymentForOrder = paymentSourceType === PAYMENT_SOURCE_TYPE.ORDER;
  return (
    <div className='md:w-1/2 bg-planner-cart-bg rounded-b-2xl md:rounded-bl-none md:rounded-r-2xl p-4 md:p-8 md:h-112'>
      <Text
        {...{
          className: 'text-base md:text-xl text-white font-medium mb-4',
          content: 'Payment Details'
        }}
      />
      <div className='border border-white text-white rounded-xl p-4'>
        <div className='flex flex-col gap-4'>
          <OrderTotal
            {...{ orderTotal: parseFormatPriceValueFromAPI(orderTotal) }}
          />
          <div className='border-t'></div>
          <PriceBreakUp
            {...{
              label: paymentPriceBreakUpLabel,
              price: parseFormatPriceValueFromAPI(amountPaid),
              sublabel: 'Advance payment'
            }}
          />
          {isPaymentForOrder && (
            <PriceBreakUp
              {...{
                label: duePriceBreakUpLabel,
                price: parseFormatPriceValueFromAPI(amountDue),
                sublabel: 'Due before Event date'
              }}
            />
          )}
        </div>
      </div>
      {showNoDuePaymentMessage ? (
        <NoDueOrderMessage />
      ) : (
        <Button
          {...{
            children: payAmountCTALabel,
            className:
              'text-brand text-sm font-medium bg-white mt-6 py-4 rounded-lg',
            onClick: onClickPayAmount
          }}
        />
      )}
      <Button
        {...{
          children: loginAndViewDetailsCTALabel,
          className:
            'text-white text-sm font-medium border border-white py-3 my-4 rounded-lg',
          onClick: onClickLoginAndViewDetails
        }}
      />
    </div>
  );
};

const PublicPaymentModal = ({
  amountDue,
  amountPaid,
  checkoutEvent: { iconUrl: eventImage, name: eventName },
  duePriceBreakUpLabel,
  eventDetails: { eventContactName, eventDate },
  host: {
    email: hostEmail,
    hostCreditEntity,
    mobile: hostMobile,
    name: hostName
  },
  itemNumber,
  loginAndViewDetailsCTALabel,
  onClickLoginAndViewDetails,
  onClickPayAmount,
  orderTotal,
  payAmountCTALabel,
  paymentPriceBreakUpLabel,
  paymentSourceType,
  renderTapPaymentWidget,
  setLoading,
  setShowToast,
  setTapSanitizedToken
}) => {
  const transactionAmountMap = {
    [PAYMENT_SOURCE_TYPE.ORDER]: amountDue,
    [PAYMENT_SOURCE_TYPE.CART]: orderTotal
  };

  const transactionAmountInUpperDenomination = parseFormatPriceValueFromAPI(
    transactionAmountMap[paymentSourceType]
  );
  return (
    <div className='flex h-screen bg-payment-modal payment-modal-bg-image-web'>
      <div className='md:block hidden payment-modal-overlayer'></div>
      <div
        className={`relative z-10 w-full md:w-56.25 bg-white bg-payment-modal-mobile payment-modal-bg-image-mobile rounded-2xl mx-auto md:mt-32 md:h-112`}
      >
        <div className='block md:hidden payment-modal-overlayer'></div>
        <div className='relative z-10 flex flex-col md:flex-row m-2 md:m-0 pt-10 md:pt-0'>
          <EventDetails
            {...{
              eventContactName,
              eventDate,
              eventImage: { url: eventImage },
              eventName,
              hostCreditEntity,
              itemNumber,
              paymentSourceType
            }}
          />
          {renderTapPaymentWidget ? (
            <TapPaymentWidget
              {...{
                containerClass:
                  'md:w-1/2 bg-planner-cart-bg rounded-b-2xl md:rounded-bl-none md:rounded-r-2xl p-4 md:p-8 md:h-112',
                headerColorClass: 'text-white',
                hostEmail,
                hostMobile,
                hostName,
                onClickPayAmount,
                renderCloseIcon: false,
                sdkContainerAdditionalClass: 'grid',
                separatorColor: 'text-white',
                setLoading,
                setShowToast,
                setTapSanitizedToken,
                showHeader: true,
                transactionAmountInUpperDenomination
              }}
            />
          ) : (
            <PaymentDetails
              {...{
                amountDue,
                amountPaid,
                duePriceBreakUpLabel,
                loginAndViewDetailsCTALabel,
                onClickLoginAndViewDetails,
                onClickPayAmount,
                orderTotal,
                payAmountCTALabel,
                paymentSourceType,
                paymentPriceBreakUpLabel
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentModal;
